/**
 * Dump universel d'exercices générés pour audit LLM.
 *
 * Pour chaque format d'un gen donné, génère N exercices et les formatte
 * selon leur type (QCM, V/F, Relier, Classer, Saisie, Trous).
 *
 * Usage console :
 *   dumpExercices(596)                       → tous les formats, 30 itérations
 *   dumpExercices(596, 50)                   → tous les formats, 50 itérations
 *   dumpExercices(596, 30, 'devinette_qcm_unique')  → un seul format
 */

import { generateExercice, getCategories } from '../index';

// ─── Formatters par type d'exercice ───

const stripHtml = (s) => (s || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

const formatQcm = (payload) => {
    const texte = stripHtml(payload.texte);
    const choix = payload.contenu?.choix || [];
    const br = payload.contenu?.bonne_reponse;
    // Résoudre la bonne réponse : lettre → valeur
    let correctVal = br;
    if (typeof br === 'string' && br.length === 1) {
        const idx = br.charCodeAt(0) - 65;
        correctVal = choix[idx];
    } else if (Array.isArray(br)) {
        correctVal = br.map(l => choix[l.charCodeAt(0) - 65]).join(', ');
    }
    return `[QCM] ${texte}\n       choix: ${JSON.stringify(choix)}\n       correct: ${br}  (=${correctVal})`;
};

const formatVF = (payload) => {
    const texte = stripHtml(payload.texte);
    const vfTable = payload.elements_additionnels?.elements?.find(e => e.config?.type === 'vraiFaux');
    if (!vfTable) return `[V/F ?] ${texte}`;
    const lignes = vfTable.config.lignes || [];
    const bonnes = vfTable.config.bonnesReponses || [];
    const propos = lignes.map((l, i) =>
        `       [${bonnes[i] === 0 ? 'VRAI' : 'FAUX'}] ${l}`
    ).join('\n');
    return `[V/F] ${texte}\n${propos}`;
};

const formatSaisie = (payload) => {
    const texte = stripHtml(payload.texte);
    const br = payload.contenu?.bonne_reponse;
    const suffixe = payload.contenu?.suffixe ? ` ${payload.contenu.suffixe}` : '';
    return `[Saisie answer=${br}${suffixe}] ${texte}`;
};

const formatRelier = (payload) => {
    const texte = stripHtml(payload.texte);
    const pairs = payload.contenu?.pairs || [];
    const leurres = payload.contenu?.cibles_leurres || [];
    const lines = pairs.map(p => `       "${p.source}" → "${p.target}"`).join('\n');
    const leurreLines = leurres.map(l => `       (leurre) "${l.target}" [${l.code}]`).join('\n');
    return `[Relier] ${texte}\n${lines}${leurreLines ? `\n${leurreLines}` : ''}`;
};

const formatClasser = (payload) => {
    const texte = stripHtml(payload.texte);
    const cats = payload.contenu?.categories || [];
    const lines = cats.map(c => `       ${c.name}: [${(c.items || []).join(', ')}]`).join('\n');
    return `[Classer] ${texte}\n${lines}`;
};

const formatTexteTrou = (payload) => {
    const texte = stripHtml(payload.texte);
    const phrase = payload.contenu?.phrase || '';
    const mode = payload.contenu?.mode || 'drag';
    const distr = payload.contenu?.distracteurs || [];
    const distrLine = mode === 'drag' ? `\n       distracteurs: ${JSON.stringify(distr)}` : '';
    return `[Trous mode=${mode}] ${texte}\n       phrase: "${phrase}"${distrLine}`;
};

const formatOrdonner = (payload) => {
    const texte = stripHtml(payload.texte);
    const items = payload.contenu?.items || [];
    return `[Ordonner] ${texte}\n       ordre correct: [${items.join(' < ')}]`;
};

/** Détecte le type du payload et applique le bon formatter. */
const formatPayload = (payload) => {
    if (!payload) return '[VIDE]';
    const vfTable = payload.elements_additionnels?.elements?.find(e => e.config?.type === 'vraiFaux');
    if (vfTable) return formatVF(payload);

    const type = payload.type_question || payload.type;
    const contenu = payload.contenu || {};

    if (type === 'qcm' && Array.isArray(contenu.choix)) return formatQcm(payload);
    if (type === 'texte') return formatSaisie(payload);
    if (type === 'association') {
        const sub = contenu.subType;
        if (sub === 'relier') return formatRelier(payload);
        if (sub === 'classer') return formatClasser(payload);
        if (sub === 'texte_trou') return formatTexteTrou(payload);
        if (sub === 'ordonner') return formatOrdonner(payload);
    }
    return `[${type || '?'}] ${stripHtml(payload.texte)}`;
};

/**
 * Dump N itérations de tous les formats d'un gen (ou d'un format spécifique).
 * Télécharge automatiquement le résultat en .txt.
 */
export const dumpExercices = (attenduId, iterations = 20, onlyFormat = null) => {
    const categories = getCategories(attenduId);
    if (!categories.length) {
        console.error(`Aucune catégorie pour l'attendu ${attenduId}`);
        return;
    }

    const output = [];
    output.push(`# Dump gen_${attenduId} — ${iterations} itérations par format`);
    output.push(`# Généré le ${new Date().toLocaleString('fr-FR')}\n`);

    let totalFormats = 0;
    categories.forEach(cat => {
        cat.formatsList.forEach(fmt => {
            if (onlyFormat && fmt.key !== onlyFormat) return;
            totalFormats++;

            output.push(`\n═══════════════════════════════════════════════════════════`);
            output.push(`  FORMAT: ${fmt.key}  (${fmt.label}) — Catégorie: ${cat.label}`);
            output.push(`═══════════════════════════════════════════════════════════\n`);

            for (let i = 0; i < iterations; i++) {
                try {
                    const payload = generateExercice(attenduId, fmt.key);
                    const formatted = formatPayload(payload);
                    const num = String(i + 1).padStart(3, ' ');
                    output.push(`${num}. ${formatted}`);
                } catch (err) {
                    output.push(`${String(i + 1).padStart(3, ' ')}. [CRASH] ${err.message}`);
                }
            }
        });
    });

    const txt = output.join('\n');

    // Télécharger
    const filename = onlyFormat
        ? `gen_${attenduId}_${onlyFormat}.txt`
        : `gen_${attenduId}_dump.txt`;
    const blob = new Blob([txt], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    console.log(`✅ Dump gen_${attenduId} : ${totalFormats} format(s) × ${iterations} itérations → ${filename}`);
};

if (typeof window !== 'undefined') {
    window.dumpExercices = dumpExercices;
}
